import { t } from 'ttag'
import type { components } from 'generated/api'

export type Plan = components['schemas']['Respondent']['plan']
export type ModuleKey = Plan['modules'][number]['module']

export type ModuleStatuses = Partial<
  Record<ModuleKey, { status?: string } | null | undefined>
>

const requiredKeys = (plan: Plan): ModuleKey[] =>
  plan.modules.filter(m => m.required).map(m => m.module)

// An empty required list would be "finished" from the start, so it never is
export const isFinished = (plan: Plan, modules: ModuleStatuses) => {
  const required = requiredKeys(plan)
  return (
    required.length > 0 &&
    required.every(key => modules[key]?.status === 'completed')
  )
}

export const hasModule = (plan: Plan | undefined, key: ModuleKey) =>
  plan?.modules.some(m => m.module === key) ?? false

const testNames = (): Partial<Record<ModuleKey, string>> => ({
  personality: t`Personality Test`,
  gma: t`Ability Test`,
  interest: t`Interest Test`,
})

const sameSet = (a: ModuleKey[], b: ModuleKey[]) =>
  a.length === b.length && b.every(key => a.includes(key))

// The sentences existing candidates see are kept as they are. Any other
// combination of required tests gets the generic sentence.
export const subtitle = (plan: Plan, finished: boolean): string => {
  const required = requiredKeys(plan)
  if (finished) {
    if (plan.completion_message !== 'recruiter_will_contact') {
      return t`You have completed both required tests.`
    }
    return required.length === 1
      ? t`You have completed the required test. The recruitment manager will contact you to inform you about the next steps.`
      : t`You have completed both tests. The recruitment manager will contact you to inform you about the next steps.`
  }
  if (sameSet(required, ['personality', 'gma'])) {
    return t`You need to take both tests to complete the process.`
  }
  if (sameSet(required, ['personality', 'interest'])) {
    return t`You need to take Personality and Interest tests to complete the process.`
  }
  const names = required
    .map(key => testNames()[key])
    .filter(Boolean)
    .join(', ')
  return t`You need to take the following tests to complete the process: ${names}`
}
